import { useJumboDialog } from '@jumbo/components/JumboDialog/hooks/useJumboDialog';
import useJumboAuth from '@jumbo/hooks/useJumboAuth';
import ArticleIcon from "@mui/icons-material/Article";
import EditIcon from '@mui/icons-material/Edit';
import { Button, Pagination } from '@mui/material';
import { DataGridPro, GridToolbar } from '@mui/x-data-grid-pro';
import subTaskService from 'app/services/subtask_service';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { NavLink, useSearchParams } from 'react-router-dom';
import ExpenseDocumentBox from '../HrOfficeExpanses/ExpenseDocumentBox';
import OpExpenseApprovalForm from './OpExpenseApprovalForm';
import { opExpenseApprovalData, OpApproveExpenseCleanUp } from 'app/redux/actions/subTaskAction';
import AllOpPaymentStatusForm from './AllOpPaymentStatusForm';
import Swal from 'sweetalert2';
import { docBaseUrl } from 'app/services/config';

// Constants
const PAGE_SIZE = 10;
const INITIAL_PAGE = 1;
const INITIAL_OFFSET = 0;

export default function OpExpenseApprove() {
    const dispatch = useDispatch();
    const { setAuthToken } = useJumboAuth();
    const { opExpensesData } = useSelector(state => state.typereducer);
    const [searchParams, setSearchParams] = useSearchParams();

    // State initialization with URL params
    const [page, setPage] = useState(Number(searchParams.get('page')) || INITIAL_PAGE);
    const [offset, setOffset] = useState((page - 1) * PAGE_SIZE);
    const [totalCount, setTotalCount] = useState(0);
    const [queryOptions, setQueryOptions] = useState({});
    const { hideDialog, showDialog } = useJumboDialog();
    const [rowSelectionModel, setRowSelectionModel] = useState([]);

    const roleCode = localStorage.getItem("roleCode");

    // Update URL parameters
    const updateURLParams = useCallback((updates) => {
        const newParams = new URLSearchParams(searchParams);

        Object.entries(updates).forEach(([key, value]) => {
            if (value === undefined || value === '' || value === null) {
                newParams.delete(key);
            } else {
                newParams.set(key, value.toString());
            }
        });

        setSearchParams(newParams);
    }, [searchParams, setSearchParams]);

    // Memoized rows data
    const rows = useMemo(() => {
        return opExpensesData.map((item, index) => ({
            id: index + 1 + offset,
            vendorName: item?.vendor_id.vendor_name,
            vendorPan: item?.vendor_id.pan_tan,
            tdsPercent: item?.vendor_id.tds_amount,
            vendor_id: item?.vendor_id.id,
            expenseId: item?.id,
            expenseAmount: item?.expense_amount,
            expenseDate: item?.expense_date ? moment(item?.expense_date).format('DD MMM YYYY') : '',
            expenseDescription: item?.expense_description || '--',
            expenseNote: item?.expense_note || '--',
            paymentStatus: item?.payment_status || 'Unpaid',
            paidBy: item?.paid_by || '--',
            paymentDate: item?.payment_date ? moment(item?.payment_date).format('DD MMM YYYY') : '',
            expenseDoc: item?.bill_reciept_documents || '--',
            status: item?.status || 'Requested',
            actionRemark: item?.action_remark || '--',
            addedBy: item?.created_by?.name || '--',
            sgstAmount: item?.sgst_amount || '--',
            cgstAmount: item?.cgst_amount || '--',
            igstAmount: item?.igst_amount || '--',
            tdsAmount: item?.tds_amount || '--',
            totalGST: item?.total_gst || '--',
            amountWithTax: item?.amount_with_tax || '--',
            payableAmount: item?.payable_amount || '--',
            invoiceNumber: item?.invoice_number || '--',
            company: item?.company || '--',
            projects: item?.projects || '--',
        }));
    }, [opExpensesData, offset]);

    // Status chip component
    const StatusChip = ({ status }) => {
        const chipColor = {
            Requested: 'blue',
            Approved: 'green',
            Rejected: 'red'
        }[status] || 'gray';

        return (
            <div style={{ display: 'flex', alignItems: 'center' }}>
                <div style={{
                    width: 10,
                    height: 10,
                    backgroundColor: chipColor,
                    marginRight: 8,
                    borderRadius: '100%',
                }}></div>
                {status}
            </div>
        );
    };


    // Dialog handlers
    const showExpenseDialog = (expenseDoc) => {
        showDialog({
            title: "Expense Documents",
            content: <ExpenseDocumentBox expenseDoc={expenseDoc} />
        });
    };

    const handleExpenseUpdate = useCallback(() => {
        hideDialog();
    }, [hideDialog]);

    const showApproveExpenseDialog = useCallback((expenseId, status, vendor_id) => {
        showDialog({
            title: "Expense",
            content: <OpExpenseApprovalForm expenseId={expenseId} status={status} onSave={handleExpenseUpdate} vendor_id={vendor_id} />
        });
    }, [handleExpenseUpdate, showDialog]);

    const handlePaymentUpdate = useCallback(() => {
        hideDialog();
    }, [hideDialog]);

    const showAddLeaveDialogPayment = useCallback((expenseIds, status) => {
        showDialog({
            title: "Add Payment Status",
            content: <AllOpPaymentStatusForm expenseIds={expenseIds} status={status} onSave={handlePaymentUpdate} />
        });
    }, [handlePaymentUpdate, showDialog]);

    // Pagination handler
    const handlePageChange = (event, value) => {
        setPage(value);
        const newOffset = (value - 1) * PAGE_SIZE;
        setOffset(newOffset);
        updateURLParams({ page: value });
    };

    // Filter handler
    const onFilterChange = useCallback((filterModel) => {
        setQueryOptions({ filterModel: { ...filterModel } });

        // Update URL with filter parameters
        const filterParams = {};
        if (filterModel?.items) {
            filterModel.items.forEach((item, index) => {
                if (item.field) {
                    filterParams[`filter_${item.field}`] = item.value || '';
                }
            });
        }
        updateURLParams({ ...filterParams, page: 1 });

        if (filterModel?.items?.[0]?.value != undefined) {
            setOffset(0);
            setPage(1);
        }
    }, [updateURLParams]);

    // Column definitions
    const columns = useMemo(() => [
        {
            field: 'id',
            headerName: 'ID',
            sortable: false,
            hideable: false,
            filterable: false,
            flex: 100,
            maxWidth: 100,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <span>{params.value}</span>
                    {params.row.status === 'Requested' && ['FINANCEMANAGER','ACCOUNTSMANAGER'].includes(roleCode) && (
                        <NavLink>
                            <EditIcon
                                onClick={() => showApproveExpenseDialog(params.row.expenseId, params.row.status, params.row.vendor_id)}
                                style={{
                                    width: '20px',
                                    height: '20px',
                                    marginLeft: '5px',
                                    cursor: 'pointer'
                                }}
                                color="action"
                            />
                        </NavLink>
                    )}
                </div>
            ),
            renderHeader: () => (
                <div style={{ whiteSpace: 'normal', wordWrap: 'break-word', textAlign: 'center', lineHeight: '1', marginTop: '2px' }}>
                    <span>ID</span>
                </div>
            ),
        },
        {
            field: 'vendorName',
            headerName: 'Vendor Name',
            sortable: false,
            flex: 220,
            align: 'center',
            headerAlign: 'center',
            renderHeader: () => (
                <div style={{ whiteSpace: 'normal', wordWrap: 'break-word', textAlign: 'center', lineHeight: '1', marginTop: '2px' }}>
                    <span>Vendor Name</span>
                </div>
            ),
        },
        {
            field: 'status',
            headerName: 'Status',
            sortable: false,
            flex: 300,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => <StatusChip status={params.value} />,
            renderHeader: () => (
                <div style={{ whiteSpace: 'normal', wordWrap: 'break-word', textAlign: 'center', lineHeight: '1', marginTop: '2px' }}>
                    <span>Status</span>
                </div>
            ),
        },
        {
            field: 'invoiceNumber',
            headerName: 'Invoice No.',
            sortable: false,
            filterable: false,
            flex: 220,
            align: 'center',
            headerAlign: 'center',
            renderHeader: () => (
                <div style={{ whiteSpace: 'normal', wordWrap: 'break-word', textAlign: 'center', lineHeight: '1', marginTop: '2px' }}>
                    <span>Invoice No.</span>
                </div>
            ),
        },
        {
            field: 'actionRemark',
            headerName: 'Remark',
            filterable: false,
            sortable: false,
            flex: 300,
            align: 'center',
            headerAlign: 'center',
            renderHeader: () => (
                <div style={{ whiteSpace: 'normal', wordWrap: 'break-word', textAlign: 'center', lineHeight: '1', marginTop: '2px' }}>
                    <span>Remark</span>
                </div>
            ),
        },
        {
            field: 'addedBy',
            headerName: 'Added By',
            sortable: false,
            filterable: false,
            flex: 220,
            align: 'center',
            headerAlign: 'center',
            renderHeader: () => (
                <div style={{ whiteSpace: 'normal', wordWrap: 'break-word', textAlign: 'center', lineHeight: '1', marginTop: '2px' }}>
                    <span>Added By</span>
                </div>
            ),
        },
        {
            field: 'expenseAmount',
            headerName: 'Expense Amount',
            sortable: false,
            flex: 220,
            align: 'center',
            headerAlign: 'center',
            renderHeader: () => (
                <div style={{ whiteSpace: 'normal', wordWrap: 'break-word', textAlign: 'center', lineHeight: '1', marginTop: '2px' }}>
                    <span>Expense Amount</span>
                </div>
            ),
        },
        {
            field: 'expenseDate',
            headerName: 'Expense Date',
            sortable: false,
            filterable: false,
            flex: 220,
            align: 'center',
            headerAlign: 'center',
            renderHeader: () => (
                <div style={{ whiteSpace: 'normal', wordWrap: 'break-word', textAlign: 'center', lineHeight: '1', marginTop: '2px' }}>
                    <span>Expense Date</span>
                </div>
            ),
        },
        {
            field: 'expenseDescription',
            headerName: 'Description',
            filterable: false,
            sortable: false,
            flex: 300,
            align: 'center',
            headerAlign: 'center',
            renderHeader: () => (
                <div style={{ whiteSpace: 'normal', wordWrap: 'break-word', textAlign: 'center', lineHeight: '1', marginTop: '2px' }}>
                    <span>Description</span>
                </div>
            ),
        },
        {
            field: 'expenseNote',
            headerName: 'Note',
            filterable: false,
            sortable: false,
            flex: 300,
            align: 'center',
            headerAlign: 'center',
            renderHeader: () => (
                <div style={{ whiteSpace: 'normal', wordWrap: 'break-word', textAlign: 'center', lineHeight: '1', marginTop: '2px' }}>
                    <span>Note</span>
                </div>
            ),
        },
        {
            field: 'paymentStatus',
            headerName: 'Payment Status',
            type: 'singleSelect',
            valueOptions: ['Paid', 'Unpaid'],
            sortable: false,
            flex: 300,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => (
                <Button
                    size="small"
                    onClick={
                        params.row.status === 'Approved' &&
                            params.row.paymentStatus !== 'Paid' &&
                            (roleCode === 'FINANCEMANAGER' || roleCode === 'ACCOUNTSMANAGER')
                            ? () => showAddLeaveDialogPayment(params.row.expenseId, params.row.status)
                            : undefined
                    }
                    disabled={
                        params.row.status !== 'Approved' ||
                        params.row.paymentStatus !== 'Unpaid' ||
                        !(roleCode === 'FINANCEMANAGER' || roleCode === 'ACCOUNTSMANAGER')
                    }
                >
                    {params.value}
                </Button>
            ),
            renderHeader: () => (
                <div style={{ whiteSpace: 'normal', wordWrap: 'break-word', textAlign: 'center', lineHeight: '1', marginTop: '2px' }}>
                    <span>Payment Status</span>
                </div>
            ),
        },
        {
            field: 'paymentDate',
            headerName: 'Payment Date',
            type: 'date',
            sortable: false,
            flex: 300,
            align: 'center',
            headerAlign: 'center',
            valueGetter: (params) => {
                const date = new Date(params.value);
                return isNaN(date.getTime()) ? new Date() : date;
            },
            renderHeader: () => (
                <div style={{ whiteSpace: 'normal', wordWrap: 'break-word', textAlign: 'center', lineHeight: '1', marginTop: '2px' }}>
                    <span>Payment Date</span>
                </div>
            ),
        },
        {
            field: 'vendorPan',
            headerName: 'Vendor PAN',
            filterable: true,
            sortable: false,
            flex: 300,
            align: 'center',
            headerAlign: 'center',
            renderHeader: () => (
                <div style={{ whiteSpace: 'normal', wordWrap: 'break-word', textAlign: 'center', lineHeight: '1', marginTop: '2px' }}>
                    <span>Vendor PAN</span>
                </div>
            ),
        },
        {
            field: 'tdsPercent',
            headerName: 'TDS Percent',
            filterable: false,
            sortable: false,
            flex: 300,
            align: 'center',
            headerAlign: 'center',
            renderHeader: () => (
                <div style={{ whiteSpace: 'normal', wordWrap: 'break-word', textAlign: 'center', lineHeight: '1', marginTop: '2px' }}>
                    <span>TDS Percent</span>
                </div>
            ),
        },
        {
            field: 'tdsAmount',
            headerName: 'TDS Amount',
            filterable: false,
            sortable: false,
            flex: 300,
            align: 'center',
            headerAlign: 'center',
            renderHeader: () => (
                <div style={{ whiteSpace: 'normal', wordWrap: 'break-word', textAlign: 'center', lineHeight: '1', marginTop: '2px' }}>
                    <span>TDS Amount</span>
                </div>
            ),
        },
        {
            field: 'totalGST',
            headerName: 'Total GST',
            filterable: false,
            sortable: false,
            flex: 300,
            align: 'center',
            headerAlign: 'center',
            renderHeader: () => (
                <div style={{ whiteSpace: 'normal', wordWrap: 'break-word', textAlign: 'center', lineHeight: '1', marginTop: '2px' }}>
                    <span>Total GST</span>
                </div>
            ),
        },
        {
            field: 'amountWithTax',
            headerName: 'Amount with tax',
            filterable: false,
            sortable: false,
            flex: 300,
            align: 'center',
            headerAlign: 'center',
            renderHeader: () => (
                <div style={{ whiteSpace: 'normal', wordWrap: 'break-word', textAlign: 'center', lineHeight: '1', marginTop: '2px' }}>
                    <span>Amount with tax</span>
                </div>
            ),
        },
        {
            field: 'payableAmount',
            headerName: 'Payable Amount',
            filterable: false,
            sortable: false,
            flex: 300,
            align: 'center',
            headerAlign: 'center',
            renderHeader: () => (
                <div style={{ whiteSpace: 'normal', wordWrap: 'break-word', textAlign: 'center', lineHeight: '1', marginTop: '2px' }}>
                    <span>Payable Amount</span>
                </div>
            ),
        },
        {
            field: 'projects', headerName: 'Projects', filterable: true, sortable: false, flex: 300, align: 'center', headerAlign: 'center', renderHeader: () => (
                <div style={{ whiteSpace: 'normal', wordWrap: 'break-word', textAlign: 'center', lineHeight: '1', marginTop: '2px' }}>
                    <span>Projects</span>
                </div>
            ),
        },
        {
            field: 'company', headerName: 'Company', filterable: true, sortable: false, flex: 300, align: 'center', headerAlign: 'center', renderHeader: () => (
                <div style={{ whiteSpace: 'normal', wordWrap: 'break-word', textAlign: 'center', lineHeight: '1', marginTop: '2px' }}>
                    <span>Company</span>
                </div>
            ),
        },
        {
            field: 'expenseDoc',
            headerName: 'Documents',
            filterable: false,
            sortable: false,
            flex: 300,
            align: 'center',
            headerAlign: 'center',
            renderCell: (params) => (
                params.row.expenseDoc.length > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <ArticleIcon
                            onClick={() => showExpenseDialog(params.value)}
                            style={{ width: '20px', height: '20px', marginLeft: '5px' }}
                            color="action"
                        />
                    </div>
                )
            ),
            renderHeader: () => (
                <div style={{ whiteSpace: 'normal', wordWrap: 'break-word', textAlign: 'center', lineHeight: '1', marginTop: '2px' }}>
                    <span>Documents</span>
                </div>
            ),
        },
    ], [roleCode, showApproveExpenseDialog, showAddLeaveDialogPayment]);

    // Export handler
    const handleExport = useCallback(async () => {
        try {
            let filterQuery = '';
            if (queryOptions?.filterModel?.items) {
                filterQuery = queryOptions.filterModel.items
                    .filter(item => item.field !== undefined)
                    .map(item => `&${item.field}=${item.value || ''}`)
                    .join('');
            }

            const data = await subTaskService.getOpExpensesApprovals(filterQuery);

            const mockData = data?.data?.data?.map((item, index) => ({
                "id": index + 1,
                "vendorName": item?.vendor_id.vendor_name || '--',
                "vendorPan": item?.vendor_id.pan_tan || '--',
                "vendorType": item?.vendor_id.vendor_type || '--',
                "companyName": item?.vendor_id.company_name || '--',
                "GSTNo": item?.vendor_id.gst_no || '--',
                "PANNo": /^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(item?.vendor_id.pan_tan) ? item?.vendor_id.pan_tan : '--',
                "TANNo": /^[A-Z]{4}[0-9]{5}[A-Z]{1}$/.test(item?.vendor_id.pan_tan) ? item?.vendor_id.pan_tan : '--',
                "billingDetails": item?.vendor_id.billing_details || '--',
                "status": item?.status || 'Requested',
                "actionRemark": item?.action_remark || "--",
                "addedBy": item?.created_by?.name || '--',
                "expenseAmount": item?.expense_amount || '--',
                "paymentStatus": item?.payment_status || 'Unpaid',
                "paidBy": item?.paid_by || '--',
                "paymentDate": item?.payment_date ? moment(item?.payment_date).format('DD MMM YYYY') : '',
                "expenseDate": moment(item?.expense_date).format('DD MMM YYYY') || '--',
                "expenseDescription": item?.expense_description || '--',
                "expenseNote": item?.expense_note || '--',
                "sgstAmount": item?.sgst_amount || '--',
                "cgstAmount": item?.cgst_amount || '--',
                "igstAmount": item?.igst_amount || '--',
                "tdsPercent": item?.vendor_id.tds_amount || '--',
                "tdsAmount": item?.tds_amount || '--',
                "totalGST": item?.total_gst || '--',
                "amountWithTax": item?.amount_with_tax || '--',
                "payableAmount": item?.payable_amount || '--',
                "invoiceNumber": item?.invoice_number || '--',
                "company": item?.company || '--',
                "projects": item?.projects || '--',
                "expenseDoc": item?.bill_reciept_documents?.map((doc) =>
                    docBaseUrl + doc?.base_url + "/" + doc?.key
                ) || '--',
            }));

            const convertToCSV = (objArray) => {
                const array = typeof objArray !== 'object' ? JSON.parse(objArray) : objArray;
                let csv = '';

                for (let i = 0; i < array.length; i++) {
                    let line = '';
                    for (const index in array[i]) {
                        if (line !== '') line += ',';
                        let fieldValue = array[i][index];

                        if (Array.isArray(fieldValue)) {
                            fieldValue = fieldValue.join('\n');
                        } else if (typeof fieldValue === 'string' && fieldValue.charAt(0) === '0') {
                            fieldValue = '"' + fieldValue + '"';
                        }

                        if (typeof fieldValue === 'string' && (fieldValue.includes(',') || fieldValue.includes('\n'))) {
                            fieldValue = '"' + fieldValue.replace(/"/g, '""') + '"';
                        }

                        line += fieldValue;
                    }
                    csv += line + '\r\n';
                }

                return csv;
            };

            const exportCSVFile = (headers, items, fileTitle) => {
                if (headers) {
                    items.unshift(headers);
                }

                const jsonObject = JSON.stringify(items);
                const csv = convertToCSV(jsonObject);
                const exportedFilename = fileTitle + '.csv' || 'export.csv';
                const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });

                if (navigator.msSaveBlob) {
                    navigator.msSaveBlob(blob, exportedFilename);
                } else {
                    const link = document.createElement("a");
                    if (link.download !== undefined) {
                        const url = URL.createObjectURL(blob);
                        link.setAttribute("href", url);
                        link.setAttribute("download", exportedFilename);
                        link.style.visibility = 'hidden';
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                    }
                }
            };

            const headers = {
                id: "ID",
                vendorName: "Vendor Name",
                vendorType: "Vendor Type",
                companyName: "Company Name",
                vendorPan: "Vendor PAN",
                GSTNo: "GST No",
                PANNo: "PAN No",
                TANNo: "TAN No",
                billingDetails: "Billing Details",
                status: "Status",
                actionRemark: "Remark",
                addedBy: "Added By",
                expenseAmount: "Expense Amount",
                paymentStatus: "Payment Status",
                paidBy: "Paid From",
                paymentDate: "Payment Date",
                expenseDate: "Expense Date",
                expenseDescription: "Description",
                expenseNote: "Note",
                sgstAmount: "SGST Amount",
                cgstAmount: "CGST Amount",
                igstAmount: "IGST Amount",
                tdsPercent: "TDS Percent",
                tdsAmount: "TDS Amount",
                totalGST: "Total GST",
                amountWithTax: "Amount with tax",
                payableAmount: "Payable Amount",
                invoiceNumber: "Invoice Number",
                company: "Company",
                projects: "Projects",
                expenseDoc: "Documents"
            };

            exportCSVFile(headers, mockData, 'Operational Expense Export');
        } catch (err) {
            if (err?.response?.status === 401) {
                setAuthToken(null);
            }
        }
    }, [queryOptions, setAuthToken]);

    // Cell click handler
    const handleCellClick = (params, event) => {
        const columnId = params.field;
        if (!['id', '__check__', 'expenseDoc', 'paymentStatus'].includes(columnId)) {
            const cellValue = params.value;

            Swal.fire({
                html: `<div style="white-space: pre-wrap;">${cellValue}</div>`,
                showConfirmButton: false,
                customClass: { popup: 'my-custom-size' },
                didOpen: () => {
                    const swalPopup = Swal.getPopup();
                    if (swalPopup) {
                        swalPopup.style.width = '400px';
                    }
                },
            });
        }
    };

    // Bulk approval handler
    const handleBulkApproval = () => {
        const expenseIds = rowSelectionModel.map(itm => itm.expenseId);
        showAddLeaveDialogPayment(expenseIds, "Approved");
    };

    // Data fetching effect
    useEffect(() => {
        const fetchData = async () => {
            let filterString = '';
            if (queryOptions?.filterModel?.items) {
                filterString = queryOptions.filterModel.items
                    .filter(item => item.field !== undefined)
                    .map(item => `&${item.field}=${item.value || ''}`)
                    .join('');
            }

            try {
                const data = await subTaskService.getOpExpensesApprovals(offset, PAGE_SIZE, filterString);
                setTotalCount(data?.data?.count);
                dispatch(opExpenseApprovalData(data?.data?.data));
            } catch (err) {
                if (err?.response?.status === 401) {
                    setAuthToken(null);
                }
            }
        };

        fetchData();

        return () => {
            dispatch(OpApproveExpenseCleanUp());
        };
    }, [offset, queryOptions, dispatch, setAuthToken]);

    // Row selection handler
    const handleRowSelection = useCallback((newRowSelectionModel) => {
        const selectedRowData = newRowSelectionModel.map(itm => rows[itm - 1 - offset]);
        setRowSelectionModel(selectedRowData);
    }, [rows, offset]);

    return (
        <>
            <div style={{ height: '60px', width: '100%' }}>
                {(roleCode === 'FINANCEMANAGER' || roleCode === 'ACCOUNTSMANAGER') && rowSelectionModel.every(emp => emp.hrFinanceStatus === 'Approved') && rowSelectionModel.length > 0 && (
                    <Button variant="contained" onClick={handleBulkApproval}>
                        Payment Approval
                    </Button>
                )}
                <Button onClick={handleExport} sx={{ float: 'right' }} variant="contained">
                    Export Expenses
                </Button>
            </div>

            <div style={{ height: '100%', width: '100%' }}>
                <DataGridPro
                    checkboxSelection
                    isRowSelectable={(params) => params.row.paymentStatus === "Unpaid" && params.row.status === "Requested" && (roleCode === 'FINANCEMANAGER' || roleCode === 'ACCOUNTSMANAGER')}
                    sx={{ backgroundColor: '#F6F4FA' }}
                    rows={rows}
                    columns={columns}
                    pageSize={30}
                    autoPageSize={true}
                    autoHeight={true}
                    density='standard'
                    filterMode='server'
                    onFilterModelChange={onFilterChange}
                    hideFooter={true}
                    onRowSelectionModelChange={handleRowSelection}
                    rowSelectionModel={rowSelectionModel.map(i => i.id)}
                    onCellClick={handleCellClick}
                />

                <Pagination
                    count={Math.ceil(totalCount / PAGE_SIZE)}
                    sx={{ mt: 3, float: 'right', color: "#F6F4FA" }}
                    page={page}
                    onChange={handlePageChange}
                />
            </div>
        </>
    );
}